import React from 'react';
import { motion } from 'framer-motion';
import { Mic, MessageSquare, Camera, Sparkles, Video, Palette } from 'lucide-react';

/**
 * StudioPossibilities — "Um espaço. Diferentes possibilidades."
 * Apresenta os formatos que o Studio suportará (sem afirmar que já está pronto).
 */
const items = [
  {
    icon: Mic,
    title: 'Podcast e Videocast',
    desc: 'Ambiente pensado para conversas com áudio limpo e captação multi-câmera.',
  },
  {
    icon: MessageSquare,
    title: 'Entrevistas',
    desc: 'Cenários preparados para entrevistas jornalísticas, corporativas e institucionais.',
  },
  {
    icon: Camera,
    title: 'Fotografia',
    desc: 'Fundos, iluminação e composição para ensaios profissionais e catálogos.',
  },
  {
    icon: Sparkles,
    title: 'Produção de Conteúdo',
    desc: 'Estrutura para criadores gravarem cortes, reels, publis e séries de conteúdo.',
  },
  {
    icon: Video,
    title: 'Vídeos Institucionais',
    desc: 'Espaço adequado para apresentações de marca, treinamentos e cases.',
  },
  {
    icon: Palette,
    title: 'Cenários Personalizados',
    desc: 'Ambiente modificável — poltronas, fundos, cortinas, cores e iluminação por projeto.',
  },
];

const StudioPossibilities = () => {
  return (
    <section
      id="possibilidades"
      data-testid="studio-possibilities"
      className="relative py-24 md:py-32 bg-[#0B0B0F] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(37,64,163,0.08)_0%,rgba(0,0,0,0)_60%)]" />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-4">
            <div className="w-8 h-[1px] bg-[#2540A3]" />
            <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] font-bold text-[#2540A3]">
              O Espaço
            </span>
            <div className="w-8 h-[1px] bg-[#2540A3]" />
          </div>
          <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black text-[#F5F5F0] leading-[1.1] mb-6">
            Um espaço.{' '}
            <span className="text-[#2540A3] italic font-black">
              Diferentes possibilidades.
            </span>
          </h2>
          <p className="text-base md:text-lg text-[#A1A1AA] max-w-2xl mx-auto font-light leading-relaxed">
            Um ambiente pensado para se adaptar à sua ideia. Diferentes poltronas,
            fundos, cortinas, composições e estilos para que cada produção tenha sua
            própria identidade.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: Math.min(i * 0.06, 0.4) }}
                data-testid={`possibility-card-${i}`}
                className="group relative border border-white/8 hover:border-[#2540A3]/50 rounded-2xl p-6 md:p-7 bg-gradient-to-br from-[#141419] to-[#0B0B0F] hover:from-[#191922] hover:to-[#0B0B0F] transition-all duration-400"
              >
                <div className="w-11 h-11 rounded-xl bg-[#2540A3]/15 border border-[#2540A3]/40 flex items-center justify-center mb-4 group-hover:bg-[#2540A3]/25 group-hover:scale-110 transition-all duration-300">
                  <Icon className="w-5 h-5 text-[#2540A3]" strokeWidth={2} />
                </div>
                <h3 className="font-display text-base md:text-lg font-bold text-[#F5F5F0] mb-2 leading-tight">
                  {it.title}
                </h3>
                <p className="text-sm text-[#A1A1AA] font-light leading-relaxed">
                  {it.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StudioPossibilities;
